import React, { useCallback, useEffect, useState } from "react";

import { omit } from "lodash";
import { useNavigate } from "react-router-dom";
import { AutocompleteCloseReason } from "@mui/base";
import { yupResolver } from "@hookform/resolvers/yup";

import { useFormWithNotification } from "@covertree/hooks";
import { Toast } from "@covertree/utils";
import { StateCode } from "@covertree/consts";

import { compareAddresses } from "@covertree/features/autocompletePrecisely";
import {
  getGeoCode,
  setPropertyAddress,
  setHasSeenSurplusLinesModal,
  selectNewHomeAddress,
  useNewBusinessSelector,
  selectGeoCodeLoading,
  selectGeoCodeResult,
  selectHasSeenSurplusLinesModal,
  resetBasicPolicyDetails,
  selectIsBookConversion,
} from "@covertree/new-business-redux";
import { GeoCodeInput } from "@covertree/new-business-graphql";

import {
  CanOperateInStateQuery,
  useCanOperateInStateLazyQuery,
} from "@covertree/agent-portal-graphql";

import { useResetNewPolicy } from "@hooks/newPolicy";

import { useAppDispatch } from "@redux/hooks";
import { navigateToPolicyStep, NewPolicyRoutes } from "@routing/paths";

import { HomeAddressForm, homeAddressFormValidator } from "./form";
import { HomeAddressView } from "./HomeAddressView";

interface HomeAddressProps {
  isNextButtonVisible: boolean;
  setIsNextButtonVisible: (isVisible: boolean) => void;
  canOperateInStateQuery: ReturnType<typeof useCanOperateInStateLazyQuery>[0];
  onCanOperateInStateCompleted: (data: CanOperateInStateQuery) => void;
  setIsSequoiaInvalidAddressModalOpen: (isOpen: boolean) => void;
  setIsSurplusLinesInformationModalOpen: (isOpen: boolean) => void;
}

export function HomeAddress({
  canOperateInStateQuery,
  isNextButtonVisible,
  setIsNextButtonVisible,
  onCanOperateInStateCompleted,
  setIsSequoiaInvalidAddressModalOpen,
  setIsSurplusLinesInformationModalOpen,
}: HomeAddressProps) {
  const geoCodeResult = useNewBusinessSelector(selectGeoCodeResult);
  const isGeoCodeLoading = useNewBusinessSelector(selectGeoCodeLoading);
  const newHomeAddressValue = useNewBusinessSelector(selectNewHomeAddress);
  const isBookConversion = useNewBusinessSelector(selectIsBookConversion);
  const hasSeenSurplusLinesModal = useNewBusinessSelector(
    selectHasSeenSurplusLinesModal
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const resetPolicy = useResetNewPolicy();

  const homeAddressForm = useFormWithNotification<HomeAddressForm>({
    mode: "all",
    reValidateMode: "onSubmit",
    defaultValues: {
      address: {
        label: newHomeAddressValue.propertyAddress?.formattedAddress,
        value: newHomeAddressValue.propertyAddress,
      },
      unit: newHomeAddressValue.lotUnit,
    },
    // TODO: Would love if someone would figure out that typescript issue - most likely discrepancy between strings in validation & enums in type
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    resolver: yupResolver(homeAddressFormValidator),
  });

  const { setValue, getValues, resetField, watch } = homeAddressForm;

  const address = watch("address");
  const unit = watch("unit");

  const callCanOperateInState = useCallback(async (state: string) => {
    await canOperateInStateQuery({
      onCompleted: onCanOperateInStateCompleted,
      variables: {
        state,
      },
    });
  }, []);

  const onSubmit = useCallback(
    async (formValues: HomeAddressForm) => {
      setIsSubmitting(true);
      if (
        newHomeAddressValue.propertyAddress?.formattedAddress &&
        !compareAddresses(
          omit(newHomeAddressValue.propertyAddress, "lotUnit"),
          omit(formValues.address.value, "lotUnit")
        )
      ) {
        resetPolicy();
      }

      dispatch(
        setPropertyAddress({
          address: formValues.address.value,
          lotUnit: formValues.unit,
        })
      );

      const [geoCodeResult] = await Promise.allSettled([
        dispatch(
          getGeoCode({
            streetAddress: formValues.address.value.mainAddressLine,
            state: formValues.address.value.state,
            city: formValues.address.value.city,
            zipCode: formValues.address.value.postCode,
            ...(formValues.unit
              ? {
                  unitNumber: formValues.unit,
                }
              : {}),
          })
        ).unwrap(),
      ]);

      if (geoCodeResult?.status === "rejected") {
        if (geoCodeResult?.reason.errorType) {
          navigateToPolicyStep(
            navigate,
            NewPolicyRoutes.FAST_DECLINE,
            NewPolicyRoutes.HOME_ADDRESS,
            undefined,
            {
              errorType: geoCodeResult.reason.errorType,
              errorMessage: geoCodeResult.reason.message,
            }
          );
          return;
        } else {
          Toast.showError("Something went wrong. Please try again.");
          setIsSubmitting(false);
          return;
        }
      }

      if (
        geoCodeResult?.status === "fulfilled" &&
        formValues.address.value.state === StateCode.NC &&
        !hasSeenSurplusLinesModal
      ) {
        dispatch(setHasSeenSurplusLinesModal(true));
        setIsSurplusLinesInformationModalOpen(true);
      }

      await callCanOperateInState(formValues.address.value.state);
      setIsSubmitting(false);
    },
    [
      newHomeAddressValue,
      dispatch,
      resetPolicy,
      resetField,
      setPropertyAddress,
      hasSeenSurplusLinesModal,
    ]
  );

  const handleMapBoxOpen = useCallback(
    (geoCodeInput: GeoCodeInput) => {
      dispatch(getGeoCode(geoCodeInput));
    },
    [getGeoCode, dispatch]
  );

  // Magic, which updates unit, only when new option was picked up. Does not interfere with manual Unit input.
  const onClose = useCallback(
    (_: React.SyntheticEvent, reason: AutocompleteCloseReason) => {
      if (reason === "selectOption") {
        setTimeout(() => {
          const address = getValues("address");

          if (address?.value) {
            setValue("unit", address.value.lotUnit ?? "");
          }
        });
      }
    },
    [getValues, setValue]
  );

  const onSequoiaBlur = useCallback(() => {
    if (!isBookConversion) return;

    const newHomeAddress = newHomeAddressValue.propertyAddress;
    const address = getValues("address");
    const unit = getValues("unit");

    if (
      address.value.state !== newHomeAddress.state ||
      address.value.postCode !== newHomeAddress.postCode
    ) {
      setIsSequoiaInvalidAddressModalOpen(true);
      setValue("address", {
        label: newHomeAddress.formattedAddress,
        value: newHomeAddress,
      });
    } else {
      dispatch(
        setPropertyAddress({
          address: address.value,
          lotUnit: unit,
        })
      );
    }
  }, [getValues, setValue, newHomeAddressValue, dispatch, isBookConversion]);

  useEffect(() => {
    if (!geoCodeResult && !isGeoCodeLoading) {
      setIsNextButtonVisible(true);
    }
  }, [address, geoCodeResult, resetPolicy]);

  useEffect(() => {
    if (
      !isSubmitting &&
      address?.value?.state &&
      newHomeAddressValue?.propertyAddress?.state &&
      geoCodeResult &&
      compareAddresses(
        omit(newHomeAddressValue.propertyAddress, "lotUnit"),
        omit(address?.value, "lotUnit")
      )
    ) {
      callCanOperateInState(address?.value.state);
    }
  }, [
    isSubmitting,
    newHomeAddressValue.propertyAddress,
    address,
    geoCodeResult,
  ]);

  useEffect(() => {
    if (
      !compareAddresses(
        omit(newHomeAddressValue.propertyAddress, "lotUnit"),
        omit(address?.value, "lotUnit")
      ) &&
      !isBookConversion
    ) {
      dispatch(resetBasicPolicyDetails());
      setIsNextButtonVisible(true);
    }
  }, [newHomeAddressValue, address]);

  useEffect(() => {
    if (unit && newHomeAddressValue.lotUnit !== unit && !isBookConversion) {
      setIsNextButtonVisible(true);
    }
  }, [unit, newHomeAddressValue]);

  return (
    <HomeAddressView
      handleMapBoxOpen={handleMapBoxOpen}
      homeAddressForm={homeAddressForm}
      isNextButtonVisible={isNextButtonVisible}
      loading={isSubmitting}
      onSubmit={onSubmit}
      onClose={onClose}
      onBlur={onSequoiaBlur}
    />
  );
}
